import React, { useState } from 'react';
import axios from 'axios';
import { Analytics } from '@vercel/analytics/react';
import { Link, Route, Routes } from 'react-router-dom';
import About from './About';
import HowItWorks from './HowItWorks';
import FAQ from './FAQ';
import Blog from './Blog';
import Partnerships from './Partnerships';

function HomePage() {
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const analyzeVideo = async () => {
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.post(`${API_URL}/api/analyze`, {
        video_url: videoUrl
      });
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Analysis failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (score) => {
    if (score > 75) return '#DC2626';
    if (score > 50) return '#F59E0B';
    if (score > 25) return '#FCD34D';
    return '#10B981';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-4">
            AdverseVideo Detector AI
          </h1>
          <p className="text-xl mb-2">
            Detect cyber-adversarial content in social media videos using AI
          </p>
          <p className="text-sm opacity-90">
            Powered by GPT-4 Vision & Whisper | Research-backed technology
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="bg-white rounded-lg shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold mb-6">Analyze a Video</h2>

          <div className="mb-6">
            <label className="block text-gray-700 mb-2 font-medium">
              Enter YouTube Video URL:
            </label>
            <input
              type="text"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            />
          </div>

          <button
            onClick={analyzeVideo}
            disabled={loading || !videoUrl}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition duration-200"
          >
            {loading ? 'Analyzing... (30-60 seconds)' : 'Analyze Video'}
          </button>

          {loading && (
            <div className="mt-8 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">
                📊 Extracting metadata...<br />
                🎥 Analyzing video frames...<br />
                🔊 Processing audio...<br />
                🤖 Running AI analysis...
              </p>
            </div>
          )}

          {error && (
            <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">⚠️ Error: {error}</p>
            </div>
          )}

          {result && result.success && (
            <div className="mt-8">
              <div
                className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 mb-6 border-2"
                style={{ borderColor: getRiskColor(result.risk_score) }}
              >
                <div className="text-center">
                  <div
                    className="text-6xl font-bold mb-2"
                    style={{ color: getRiskColor(result.risk_score) }}
                  >
                    {result.risk_score}/100
                  </div>
                  <div className="text-2xl font-semibold text-gray-800">
                    {result.report.classification} RISK
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-lg mb-3 flex items-center">
                  <span className="mr-2">📹</span> Video Information
                </h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <p><strong>Title:</strong> {result.report.video_info.title}</p>
                  <p><strong>Channel:</strong> {result.report.video_info.channel}</p>
                  <p><strong>Views:</strong> {result.report.video_info.views.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-3">
                    <strong>URL:</strong>{' '}
                    <a
                      href={result.report.video_info.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      {result.report.video_info.url}
                    </a>
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-lg mb-3 flex items-center">
                  <span className="mr-2">🔍</span> Frame Analysis ({result.report.frame_analysis.length} frames)
                </h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {result.report.frame_analysis.map((frame, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm border-l-4 border-blue-500">
                      <div className="font-semibold text-gray-700 mb-1">
                        Frame {frame.frame_number}
                      </div>
                      <p className="text-gray-600 whitespace-pre-wrap">{frame.analysis}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-lg mb-3 flex items-center">
                  <span className="mr-2">📝</span> Text Analysis
                </h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{result.report.text_analysis.analysis}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => window.print()}
                  className="bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition duration-200"
                >
                  📄 Print Report
                </button>
                <button
                  onClick={() => {
                    setResult(null);
                    setVideoUrl('');
                  }}
                  className="bg-gray-600 text-white py-3 rounded-lg font-semibold hover:bg-gray-700 transition duration-200"
                >
                  🔄 Analyze Another Video
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="text-center p-6 bg-white rounded-lg shadow">
            <div className="text-4xl mb-4">🎥</div>
            <h3 className="font-bold mb-2">Multimodal Analysis</h3>
            <p className="text-gray-600 text-sm">Analyzes video frames, audio transcripts, and metadata</p>
          </div>
          <div className="text-center p-6 bg-white rounded-lg shadow">
            <div className="text-4xl mb-4">🤖</div>
            <h3 className="font-bold mb-2">AI-Powered</h3>
            <p className="text-gray-600 text-sm">Uses GPT-4 Vision and Whisper for advanced detection</p>
          </div>
          <div className="text-center p-6 bg-white rounded-lg shadow">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="font-bold mb-2">Detailed Reports</h3>
            <p className="text-gray-600 text-sm">Comprehensive threat assessments with frame-by-frame analysis</p>
          </div>
        </div>

        <div className="bg-blue-50 rounded-lg p-8 border border-blue-200">
          <h3 className="text-xl font-bold mb-4">About This Tool</h3>
          <p className="text-gray-700 mb-4">
            AdverseVideo Detector AI is a research-backed platform developed to identify cyber-adversarial content
            in social media videos. Built on peer-reviewed research published at IEEE ISI, this tool helps:
          </p>
          <ul className="list-disc list-inside space-y-2 text-gray-700 mb-4">
            <li>Academic researchers studying cyber threats</li>
            <li>Educational institutions protecting students</li>
            <li>Small organizations monitoring security</li>
            <li>Individuals concerned about online safety</li>
          </ul>
          <p className="text-sm text-gray-600">
            <strong>Research:</strong> "Detecting Cyber-Adversarial Videos in Traditional Social Media"
            (IEEE International Conference on Intelligence and Security Informatics)
          </p>
        </div>
      </div>

      <footer className="bg-gray-800 text-white py-8 mt-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm">
            © 2026 AdverseVideo Detector AI | Non-profit research platform
          </p>
          <p className="text-xs text-gray-400 mt-2">
            Built by Bingyan Du | adversevideo.org
          </p>
          <p className="text-xs text-gray-300 mt-2">
            <a href="/privacy.html" className="hover:underline">
              Privacy Policy
            </a>
            {' | '}
            <Link to="/about" className="hover:underline">
              About
            </Link>
            {' | '}
            <Link to="/how-it-works" className="hover:underline">
              How It Works
            </Link>
            {' | '}
            <Link to="/faq" className="hover:underline">
              FAQ
            </Link>
            {' | '}
            <Link to="/blog" className="hover:underline">
              Blog
            </Link>
            {' | '}
            <Link to="/partnerships" className="hover:underline">
              Partnerships
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/about" element={<About />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/partnerships" element={<Partnerships />} />
      </Routes>
      <Analytics />
    </>
  );
}

export default App;